
import { defineComponent, type PropType } from "vue"
import MarketingCampaign, { MarketingPlanningSaveError } from "packs/models/MarketingCampaign"
import { buildAudienceDraft } from "./marketingSetupDrafts"

const DEFAULT_CTR_PERCENT: Record<string, number> = {
    "1_3": 12,
    "4_5": 7,
    "6_10": 3.5,
    "11_20": 1.5,
    "21_plus": 0.5,
}

const lines = (value: any) => `${value || ""}`.split(/\r?\n/).map(item => item.trim()).filter(Boolean)
const listText = (value: any) => Array.isArray(value) ? value.join("\n") : `${value || ""}`
// 表記ゆれ（全角空白・大文字小文字・連続空白）を同じキーワードとして扱う。
const keywordKey = (value: any) => `${value || ""}`.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim()
const MEASUREMENT_EVENTS = [
    "article_view", "article_cta_impression", "article_cta_click", "service_lp_arrival",
    "form_view", "form_start", "form_complete", "booking_confirmed",
]
// Marketing::CampaignSetupAssessment.target_keyword_requirements と同じ判定・表示名（未保存の下書きにも即時に出すため）。
// 必須は基本3項目だけ。詳細項目は任意で、入力済みの数だけを表示する。
const DETAIL_FIELDS: ((target: any) => boolean)[] = [
    target => !!`${target.keyword_cluster || ""}`.trim(),
    target => !!target.search_intent,
    target => !!target.purchase_stage,
    target => !!`${target.search_context || ""}`.trim(),
    target => !!`${target.job_to_be_done || ""}`.trim(),
    target => !!`${target.expected_answer || ""}`.trim(),
    target => [target.pains_text, target.barriers_text, target.anxieties_text, target.objections_text].some(value => lines(value).length > 0),
    target => !!`${target.decision_evidence || ""}`.trim() || !!`${target.verification_method || ""}`.trim(),
    target => [target.excluded_terms_text, target.non_target_intents_text].some(value => lines(value).length > 0),
]
const TEMPLATE_EVIDENCE = "戦略書2026下期-2028 06章 SEO固定リスト。要一次調査"
const TEMPLATE_URL_PLACEHOLDER = "候補: https://link.waaq.jp/chukai_agent"
// 戦略書の固定リストを仮説として下書きに入れる。対象ページURLは人が確認して入力する。
// 「想定読者と悩み」は人物像＋悩み1文（既存の保存値は変えない。テンプレートから新しく追加した下書きだけに入る）。
const STRATEGY_TEMPLATES = [
    {
        keyword: "人材紹介 日程調整", keyword_cluster: "人材紹介の三者間調整", search_intent: "problem",
        purchase_stage: "consideration", target_page_scope: "service_lp",
        anticipated_user: "人材紹介会社のキャリアアドバイザー・リクルーティングアドバイザー。候補者と求人企業の面談日程がメール・電話の往復で決まらず困っている。",
        search_context: "候補者と求人企業の面談日程が決まらず、メール・電話の往復で確定までに日数がかかっているとき",
        job_to_be_done: "候補者・企業・自社の三者の面談日程を、往復を減らして短い面談確定リードタイムで確定したい",
        expected_answer: "人材紹介の面談調整で往復が増える理由と、三者間調整を受け口標準化で短くする進め方",
    },
    {
        keyword: "三者間 日程調整", keyword_cluster: "人材紹介の三者間調整", search_intent: "method",
        purchase_stage: "consideration", target_page_scope: "blog_article",
        anticipated_user: "人材紹介・採用支援で候補者と企業の面談を調整する担当者。三者の空き時間を手作業で突き合わせていて調整が追いつかない。",
        search_context: "候補者・企業担当者・自社の三者の空き時間を手作業で突き合わせていて、調整が追いつかないとき",
        job_to_be_done: "三者間調整の手順を決め、誰が何を入力すれば面談が確定するかを標準化したい",
        expected_answer: "三者間調整の進め方（候補日の出し方・確定までの流れ）と、ツールで自動化できる範囲",
    },
    {
        keyword: "面接 日程調整 自動化", keyword_cluster: "採用面接の調整自動化", search_intent: "implementation",
        purchase_stage: "consideration", target_page_scope: "blog_article",
        anticipated_user: "企業の採用担当者・人事、人材紹介会社の面談調整担当。面接件数が増え、日程調整と面接官の空き確認に時間を取られている。",
        search_context: "面接件数が増え、候補者との日程調整や面接官の空き確認に時間を取られているとき",
        job_to_be_done: "面接の日程調整を自動化し、面談確定リードタイムを短くしたい",
        expected_answer: "面接日程調整を自動化する方法と、導入前に確認すべき点（カレンダー連携・面接官の割り当て）",
    },
    {
        keyword: "エージェント 面談設定 効率化", keyword_cluster: "エージェント面談の受け口標準化", search_intent: "problem",
        purchase_stage: "consideration", target_page_scope: "blog_article",
        anticipated_user: "人材紹介会社のマネージャー・キャリアアドバイザー。アドバイザーごとに面談設定のやり方が違い、受け口がばらついて困っている。",
        search_context: "アドバイザーごとに面談設定のやり方が違い、候補者との面談の受け口がばらついているとき",
        job_to_be_done: "エージェント面談の受け口を社内で標準化し、面談設定の手間とばらつきを減らしたい",
        expected_answer: "面談設定の受け口を標準化する方法と、標準化で減らせる作業",
    },
    {
        keyword: "採用 面談 ノーショー 対策", keyword_cluster: "面談のノーショー・辞退対策", search_intent: "problem",
        purchase_stage: "awareness", target_page_scope: "blog_article",
        anticipated_user: "採用担当者・人材紹介のキャリアアドバイザー。確定した面談に候補者が来ない・直前の辞退が続いて困っている。",
        search_context: "確定した面談に候補者が来ない、直前の辞退が続いているとき",
        job_to_be_done: "面談のノーショー・辞退を減らし、確定した面談を実施につなげたい",
        expected_answer: "ノーショーが起きる要因と、面談確定から実施までの間にできる対策（リマインド・日程変更の受け口）",
    },
    {
        keyword: "日程調整 Salesforce 連携", keyword_cluster: "業務システム接続の予約導線", search_intent: "implementation",
        purchase_stage: "comparison", target_page_scope: "service_lp",
        anticipated_user: "Salesforce・ATSを使う人材紹介・採用部門の運用担当者。面談・予約の情報を手入力で転記していて、手間と転記漏れに困っている。",
        search_context: "面談・予約の情報をSalesforceやATSへ手入力していて、予約導線と業務システムをつなげたいとき",
        job_to_be_done: "予約導線とSalesforce/ATSを接続し、面談確定から記録までを一続きにしたい",
        expected_answer: "日程調整とSalesforceを連携する方法と、ツールを比べるときの観点",
    },
]

export default defineComponent({
    name: "MarketingCampaignKeywordEditor",
    props: {
        campaign: { type: Object as PropType<MarketingCampaign>, required: true },
        writable: { type: Boolean, default: true },
        gscEnabled: { type: Boolean, default: true },
    },
    emits: ["saved"],
    data() {
        return {
            targets: [] as any[],
            settings: {
                fallback_ctr_percent: { ...DEFAULT_CTR_PERCENT },
                minimum_benchmark_impressions: 100,
                minimum_benchmark_queries: 3,
            } as any,
            originalPayload: "",
            saving: false,
            statusMessage: "",
            statusKind: "",
            maxTargets: 100,
            gscLoading: false,
            gscPanelOpen: false,
            gscCandidates: [] as { query: string; impressions: number; clicks: number; brand: boolean }[],
            gscPeriod: null as { from: string; to: string } | null,
            selectedGscQueries: [] as string[],
            excludeBrandQueries: true,
            gscCandidateLimit: 20,
            audienceDrafting: false,
            ctrBands: [
                { key: "1_3", label: "1〜3位" },
                { key: "4_5", label: "4〜5位" },
                { key: "6_10", label: "6〜10位" },
                { key: "11_20", label: "11〜20位" },
                { key: "21_plus", label: "21位以下" },
            ],
            eventOptions: [
                { value: "article_view", label: "記事閲覧" }, { value: "article_cta_impression", label: "記事CTA表示" },
                { value: "article_cta_click", label: "記事CTAクリック" }, { value: "service_lp_arrival", label: "サービスLP到着" },
                { value: "form_view", label: "フォーム表示" }, { value: "form_start", label: "フォーム開始" },
                { value: "form_complete", label: "フォーム完了" }, { value: "booking_confirmed", label: "予約確定" },
            ],
        }
    },
    computed: {
        campaignFinalConversionEvent(): string {
            return `${MarketingCampaign.planningOf(this.campaign).final_conversion_event || ""}`
        },
        localPlanning(): boolean {
            // remote: 本番同期値のみ / local: ローカル設定あり（どちらも本番同期施策）
            return ["local", "remote"].includes(this.campaign.planning_source)
        },
        existingKeywordKeys(): Set<string> {
            return new Set(this.targets.map((target: any) => keywordKey(target.keyword)).filter(Boolean))
        },
        visibleGscCandidates(): any[] {
            return this.gscCandidates
                .filter(candidate => !(this.excludeBrandQueries && candidate.brand))
                .slice(0, this.gscCandidateLimit)
                .map(candidate => ({ ...candidate, registered: this.existingKeywordKeys.has(keywordKey(candidate.query)) }))
        },
        gscPeriodLabel(): string {
            return this.gscPeriod ? `${this.gscPeriod.from}〜${this.gscPeriod.to}` : "直近28日"
        },
        normalizedPayload(): any {
            return {
                target_keywords: this.targets
                    .map((target: any) => {
                        const keyword = `${target.keyword || ""}`.trim()
                        const ctrPercent = Number(target.target_ctr_percent)
                        return {
                            keyword,
                            ...(target.keyword_cluster?.trim() ? { keyword_cluster: target.keyword_cluster.trim() } : {}),
                            priority: target.priority || "medium",
                            ...(target.search_intent ? { search_intent: target.search_intent } : {}),
                            ...(target.purchase_stage ? { purchase_stage: target.purchase_stage } : {}),
                            ...(target.brand_segment ? { brand_segment: target.brand_segment } : {}),
                            ...(target.anticipated_user?.trim() ? { anticipated_user: target.anticipated_user.trim() } : {}),
                            ...(target.search_context?.trim() ? { search_context: target.search_context.trim() } : {}),
                            ...(target.job_to_be_done?.trim() ? { job_to_be_done: target.job_to_be_done.trim() } : {}),
                            ...(lines(target.pains_text).length ? { pains: lines(target.pains_text) } : {}),
                            ...(lines(target.barriers_text).length ? { barriers: lines(target.barriers_text) } : {}),
                            ...(lines(target.anxieties_text).length ? { anxieties: lines(target.anxieties_text) } : {}),
                            ...(lines(target.objections_text).length ? { objections: lines(target.objections_text) } : {}),
                            ...(target.expected_answer?.trim() ? { expected_answer: target.expected_answer.trim() } : {}),
                            ...(target.target_page_url?.trim() ? { target_page_url: target.target_page_url.trim() } : {}),
                            ...(target.target_page_scope ? { target_page_scope: target.target_page_scope } : {}),
                            business_relevance: Math.max(0, Math.min(100, Number(target.business_relevance ?? 50))),
                            service_fit: Math.max(0, Math.min(100, Number(target.service_fit ?? 50))),
                            ...(Number(target.target_position) > 0 ? { target_position: Number(target.target_position) } : {}),
                            ...(target.target_ctr_percent !== "" && Number.isFinite(ctrPercent)
                                ? { target_ctr: ctrPercent / 100 }
                                : {}),
                            ...(target.memo?.trim() ? { memo: target.memo.trim() } : {}),
                            ...(target.decision_evidence?.trim() ? { decision_evidence: target.decision_evidence.trim() } : {}),
                            ...(target.verification_method?.trim() ? { verification_method: target.verification_method.trim() } : {}),
                            ...(lines(target.excluded_terms_text).length ? { excluded_terms: lines(target.excluded_terms_text) } : {}),
                            ...(lines(target.non_target_intents_text).length ? { non_target_intents: lines(target.non_target_intents_text) } : {}),
                            ...(target.final_conversion_event ? { final_conversion_event: target.final_conversion_event } : {}),
                        }
                    })
                    .filter((target: any) => target.keyword.length > 0),
                keyword_opportunity_settings: {
                    fallback_ctr_curve: Object.fromEntries(
                        Object.entries(this.settings.fallback_ctr_percent).map(([key, value]) => [key, Number(value) / 100])
                    ),
                    minimum_benchmark_impressions: Math.max(
                        20,
                        Math.min(1_000_000, Number(this.settings.minimum_benchmark_impressions || 100))
                    ),
                    minimum_benchmark_queries: Math.max(1, Math.min(100, Number(this.settings.minimum_benchmark_queries || 3))),
                },
            }
        },
        dirty(): boolean {
            return JSON.stringify(this.normalizedPayload) !== this.originalPayload
        },
        detailFieldCount(): number {
            return DETAIL_FIELDS.length
        },
        canSave(): boolean {
            return this.writable && !this.saving && this.dirty && this.targets.every((target: any) => `${target.keyword || ""}`.trim())
        },
    },
    watch: {
        "campaign.id"() {
            this.applyCampaign()
        },
    },
    mounted() {
        this.applyCampaign()
    },
    methods: {
        applyCampaign(campaign?: MarketingCampaign) {
            const source = MarketingCampaign.planningOf(campaign || this.campaign)
            this.targets = (source.target_keywords || []).map((target: any) => ({
                ...target,
                target_ctr_percent: target.target_ctr == null ? "" : Number(target.target_ctr) * 100,
                pains_text: listText(target.pains), barriers_text: listText(target.barriers),
                anxieties_text: listText(target.anxieties), objections_text: listText(target.objections),
                excluded_terms_text: listText(target.excluded_terms), non_target_intents_text: listText(target.non_target_intents),
            }))
            const configured = source.keyword_opportunity_settings || ({} as any)
            const curve = configured.fallback_ctr_curve || {}
            this.settings = {
                fallback_ctr_percent: Object.fromEntries(
                    Object.keys(DEFAULT_CTR_PERCENT).map(key => [key, Number(curve[key] ?? DEFAULT_CTR_PERCENT[key] / 100) * 100])
                ),
                minimum_benchmark_impressions: Number(configured.minimum_benchmark_impressions || 100),
                minimum_benchmark_queries: Number(configured.minimum_benchmark_queries || 3),
            }
            this.originalPayload = JSON.stringify(this.normalizedPayload)
            this.statusMessage = ""
        },
        reset() {
            this.applyCampaign()
            this.statusKind = "neutral"
            this.statusMessage = "保存前の変更を取り消しました。"
        },
        missingLabels(target: any): string[] {
            if (!`${target.keyword || ""}`.trim()) return []
            const missing: string[] = []
            if (!`${target.anticipated_user || ""}`.trim()) missing.push("想定読者と悩み")
            if (!`${target.target_page_url || ""}`.trim() && !target.target_page_scope) missing.push("対象ページ")
            const finalEvent = target.final_conversion_event || this.campaignFinalConversionEvent
            if (!MEASUREMENT_EVENTS.includes(finalEvent)) missing.push("最終成果イベント")
            return missing
        },
        filledDetailCount(target: any): number {
            return DETAIL_FIELDS.filter(filled => filled(target)).length
        },
        emptyTarget(): any {
            return {
                keyword: "", keyword_cluster: "", priority: "medium", search_intent: "", purchase_stage: "",
                brand_segment: "non_brand", anticipated_user: "", search_context: "", job_to_be_done: "",
                pains_text: "", barriers_text: "", anxieties_text: "", objections_text: "", expected_answer: "",
                target_page_url: "", target_page_scope: "", business_relevance: 50, service_fit: 50,
                target_position: "", target_ctr_percent: "", excluded_terms_text: "", non_target_intents_text: "",
                final_conversion_event: "", memo: "", decision_evidence: "", verification_method: "",
            }
        },
        // 既存（保存済み・下書き）と同じキーワードは追加しない。保存はユーザーが押す。
        appendDrafts(drafts: any[]): { added: number; skipped: number } {
            let added = 0
            let skipped = 0
            const keys = new Set(this.existingKeywordKeys)
            drafts.forEach(draft => {
                const key = keywordKey(draft.keyword)
                if (!key || keys.has(key) || this.targets.length >= this.maxTargets) {
                    skipped += 1
                    return
                }
                keys.add(key)
                this.targets.push({ ...this.emptyTarget(), ...draft, _draft: true })
                added += 1
            })
            return { added, skipped }
        },
        addStrategyTemplates() {
            if (!this.writable) return
            const result = this.appendDrafts(
                STRATEGY_TEMPLATES.map(template => ({
                    ...template,
                    decision_evidence: TEMPLATE_EVIDENCE,
                    _target_page_url_placeholder: TEMPLATE_URL_PLACEHOLDER,
                }))
            )
            this.statusKind = "neutral"
            this.statusMessage =
                `戦略テンプレートから${result.added}件を下書きに追加しました` +
                (result.skipped > 0 ? `（登録済みと同じ${result.skipped}件は追加していません）` : "") +
                "。値は仮説です。内容と対象ページURLを確認してから保存してください。"
        },
        // 同じ画面で施策目標を保存した直後でも最新のCEPを使うよう、押したときに1回だけ取得する。
        // 取得できなければ戦略のCEPで推測する。
        async goalTextForAudienceDrafts(): Promise<string> {
            try {
                const goal = await MarketingCampaign.fetchGoal(this.campaign.id)
                return `${goal?.prompt_text || ""}`
            } catch {
                return ""
            }
        },
        // 「想定読者と悩み」が空のカードにだけ決定的な下書きを入れる（外部AIは呼ばない）。保存はユーザーが押す。
        async insertAudienceDraft(index: number) {
            const target = this.targets[index]
            if (!this.writable || !target || `${target.anticipated_user || ""}`.trim() || !`${target.keyword || ""}`.trim()) return
            this.audienceDrafting = true
            try {
                const goalText = await this.goalTextForAudienceDrafts()
                if (`${target.anticipated_user || ""}`.trim()) return
                target.anticipated_user = buildAudienceDraft({
                    keyword: target.keyword,
                    targetPageUrl: target.target_page_url,
                    goalText,
                })
                this.statusKind = "neutral"
                this.statusMessage = "「想定読者と悩み」に推測の下書きを入れました。内容を確認し、必要なら書き換えてから保存してください。"
            } finally {
                this.audienceDrafting = false
            }
        },
        async toggleGscCandidates() {
            if (this.gscPanelOpen) {
                this.gscPanelOpen = false
                return
            }
            this.gscLoading = true
            try {
                const dashboard = await MarketingCampaign.fetchGscDashboard(this.campaign.id, {})
                this.gscPeriod = dashboard?.period?.current || null
                this.gscCandidates = (dashboard?.top_impression_keywords || [])
                    .filter((row: any) => `${row.query || ""}`.trim())
                    .map((row: any) => ({
                        query: `${row.query}`.trim(),
                        impressions: Number(row.current?.impressions || 0),
                        clicks: Number(row.current?.clicks || 0),
                        brand: row.brand_query == true,
                    }))
                    .sort((a: any, b: any) => b.impressions - a.impressions)
                this.selectedGscQueries = []
                this.gscPanelOpen = true
            } finally {
                this.gscLoading = false
            }
        },
        addGscCandidates() {
            const selected = this.visibleGscCandidates.filter(candidate => this.selectedGscQueries.includes(candidate.query))
            const result = this.appendDrafts(
                selected.map(candidate => ({
                    keyword: candidate.query,
                    brand_segment: candidate.brand ? "brand" : "non_brand",
                    decision_evidence: `GSC観測query（${this.gscPeriodLabel}、表示回数${candidate.impressions}）。要一次調査`,
                }))
            )
            this.selectedGscQueries = []
            this.statusKind = "neutral"
            this.statusMessage = `GSCの検索語から${result.added}件を下書きに追加しました。保存するまで反映されません。`
        },
        formatCount(value: number) {
            return Number(value || 0).toLocaleString("ja-JP")
        },
        addTarget() {
            if (this.targets.length >= this.maxTargets) return
            this.targets.push({
                keyword: "",
                keyword_cluster: "",
                priority: "medium",
                search_intent: "",
                purchase_stage: "",
                brand_segment: "non_brand",
                anticipated_user: "", search_context: "", job_to_be_done: "",
                pains_text: "", barriers_text: "", anxieties_text: "", objections_text: "", expected_answer: "",
                target_page_url: "",
                target_page_scope: "",
                business_relevance: 50,
                service_fit: 50,
                target_position: "",
                target_ctr_percent: "",
                excluded_terms_text: "", non_target_intents_text: "", final_conversion_event: "",
                memo: "",
            })
        },
        removeTarget(index: number) {
            this.targets.splice(index, 1)
        },
        async save() {
            if (!this.canSave) return
            this.saving = true
            this.statusKind = "saving"
            this.statusMessage = "保存中です。"
            try {
                const campaign = await MarketingCampaign.updatePlanning(
                    this.campaign.id,
                    this.normalizedPayload,
                    this.campaign.local_planning_version || 0
                )
                this.applyCampaign(campaign)
                this.$emit("saved", campaign)
                this.statusKind = "success"
                this.statusMessage = "獲得目標キーワードを保存しました。"
            } catch (error: any) {
                this.statusKind = "error"
                this.statusMessage =
                    error instanceof MarketingPlanningSaveError && error.status == 409
                        ? `${error.message}（入力中の内容は残っています。別の画面での変更を確認してから再度保存してください）`
                        : error?.message || "保存に失敗しました。入力内容を確認してください。"
            } finally {
                this.saving = false
            }
        },
    },
})
